import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Download, Filter, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { adminApi } from "../../api/auth";
import { Input } from "../../components/ui/input";

const MET = { paypal: "PayPal", mp_ar: "MP-AR", mp_cl: "MP-CL", khipu: "Khipu", crypto: "USDT" };
const STATUS_COLOR = {
  approved: "text-green-400 bg-green-950/30 border-green-900/40",
  pending:  "text-amber-400 bg-amber-950/30 border-amber-900/40",
  rejected: "text-red-400 bg-red-950/30 border-red-900/40",
  refunded: "text-purple-400 bg-purple-950/30 border-purple-900/40",
  cancelled:"text-[#7e7768] bg-white/5 border-[#3a3328]",
  expired:  "text-[#7e7768] bg-white/5 border-[#3a3328]",
};
const fmtDate = (s) => s ? new Date(s.replace(' ', 'T')).toLocaleString("es-AR") : "—";

export default function AdminOrders() {
  const [filters, setFilters] = useState({ status: "all", method: "", currency: "", q: "", from: "", to: "", page: 1, size: 50 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const res = await adminApi.orders(filters);
      setData(res);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: 1 }));
  const onSubmit = (e) => { e.preventDefault(); fetchData(); };

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="ornament"><span>Donaciones</span></div>
          <h1 className="section-title mt-3 text-3xl">Historial de pagos</h1>
        </div>
        <a
          href={adminApi.exportUrl("orders", filters)}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider bg-[#d4a843] text-black rounded hover:bg-[#e0b860]"
          data-testid="export-orders-csv"
        >
          <Download className="w-3.5 h-3.5" /> Exportar CSV
        </a>
      </div>

      <form onSubmit={onSubmit} className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#d4a843]">
          <Filter className="w-4 h-4" /> Filtros
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <select value={filters.status} onChange={(e) => set("status", e.target.value)}
            className="bg-[#1a1610] border border-[#3a3328] text-white text-sm rounded px-2 py-1.5"
            data-testid="filter-status">
            <option value="all">Todos</option>
            <option value="approved">Aprobadas</option>
            <option value="pending">Pendientes</option>
            <option value="rejected">Rechazadas</option>
            <option value="refunded">Reembolsadas</option>
            <option value="cancelled">Canceladas</option>
          </select>
          <select value={filters.method} onChange={(e) => set("method", e.target.value)}
            className="bg-[#1a1610] border border-[#3a3328] text-white text-sm rounded px-2 py-1.5"
            data-testid="filter-method">
            <option value="">Todos metodos</option>
            <option value="paypal">PayPal</option>
            <option value="mp_ar">MP Argentina</option>
            <option value="mp_cl">MP Chile</option>
            <option value="khipu">Khipu</option>
            <option value="crypto">Crypto</option>
          </select>
          <select value={filters.currency} onChange={(e) => set("currency", e.target.value)}
            className="bg-[#1a1610] border border-[#3a3328] text-white text-sm rounded px-2 py-1.5"
            data-testid="filter-currency">
            <option value="">Todas monedas</option>
            <option value="USD">USD</option>
            <option value="ARS">ARS</option>
            <option value="CLP">CLP</option>
          </select>
          <Input type="date" value={filters.from} onChange={(e) => set("from", e.target.value)}
            className="bg-[#1a1610] border-[#3a3328] text-white text-sm h-9" data-testid="filter-from" />
          <Input type="date" value={filters.to} onChange={(e) => set("to", e.target.value)}
            className="bg-[#1a1610] border-[#3a3328] text-white text-sm h-9" data-testid="filter-to" />
          <Input type="text" placeholder="account, email, order, txn, ip..."
            value={filters.q} onChange={(e) => set("q", e.target.value)}
            className="bg-[#1a1610] border-[#3a3328] text-white text-sm h-9" data-testid="filter-q" />
        </div>
      </form>

      {err && <div className="text-red-400 text-sm border border-red-900/40 p-3 rounded">{err}</div>}

      <div className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#7e7768] text-xs uppercase border-b border-[#3a3328] bg-black/30">
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left">Account</th>
                <th className="text-left">Order UID</th>
                <th className="text-left">Metodo</th>
                <th className="text-right">Monto</th>
                <th className="text-right">Coins</th>
                <th className="text-center">Status</th>
                <th className="text-left">IP / Pais</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-[#d4a843] inline" />
                </td></tr>
              )}
              {!loading && data?.rows?.length === 0 && (
                <tr><td colSpan={9} className="text-center text-[#7e7768] py-6">Sin resultados</td></tr>
              )}
              {!loading && data?.rows?.map((o) => (
                <tr key={o.order_uid} className="border-b border-[#3a3328]/40 hover:bg-white/[0.02]" data-testid={`order-row-${o.order_uid}`}>
                  <td className="px-3 py-2 text-[#b8b3a8] text-xs whitespace-nowrap">{fmtDate(o.created_at)}</td>
                  <td>
                    <Link to={`/admin/player/${encodeURIComponent(o.account)}`} className="text-[#d4a843] hover:underline">
                      {o.account}
                    </Link>
                  </td>
                  <td className="font-mono text-xs text-[#b8b3a8]">{o.order_uid}</td>
                  <td className="text-[#b8b3a8]">{MET[o.method] || o.method}</td>
                  <td className="text-right text-white whitespace-nowrap">{Number(o.amount).toLocaleString()} <span className="text-[#7e7768]">{o.currency}</span></td>
                  <td className="text-right text-amber-300 whitespace-nowrap">{Number(o.coins_total).toLocaleString()}</td>
                  <td className="text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${STATUS_COLOR[o.status] || ""}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="text-xs text-[#b8b3a8] whitespace-nowrap">{o.ip} {o.country && <span className="text-[#7e7768]">({o.country})</span>}</td>
                  <td className="text-right pr-3">
                    <Link to={`/admin/player/${encodeURIComponent(o.account)}`} className="text-[#d4a843] hover:text-[#e0b860]">
                      <ExternalLink className="w-3.5 h-3.5 inline" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 text-xs border-t border-[#3a3328] bg-black/20">
            <span className="text-[#7e7768]">{data.total} resultados · pagina {data.page} de {data.pages}</span>
            <div className="flex gap-1">
              <button onClick={() => set("page", Math.max(1, filters.page - 1))} disabled={filters.page <= 1}
                className="px-2 py-1 border border-[#3a3328] rounded text-[#b8b3a8] disabled:opacity-40 hover:bg-white/5">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => set("page", Math.min(data.pages, filters.page + 1))} disabled={filters.page >= data.pages}
                className="px-2 py-1 border border-[#3a3328] rounded text-[#b8b3a8] disabled:opacity-40 hover:bg-white/5">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
